import styled, { keyframes } from "styled-components";
import Video from "../../Video/Video";

// ─── Keyframes ───────────────────────────────────────────────────────────────

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const corePulse = keyframes`
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.9;
  }
  50% {
    transform: translate(-50%, -50%) scale(0.85);
    opacity: 0.6;
  }
`;

const shimmerSweep = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const buttonShimmer = keyframes`
  0% { left: -100%; }
  100% { left: 150%; }
`;

// ─── Card container — light water glass ─────────────────────────────────

export const CollectionContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${(props) => props.$aspectRatio};
  border-radius: 18px;
  overflow: hidden;
  isolation: isolate;
  contain: layout style paint;
  container-type: inline-size;
  /* will-change dropped — this card renders N times per page (one per
     collection). Each will-change forces a composited layer, so a 24-card
     page held 24 always-hot GPU layers. isolation + contain above already
     give the browser enough hint to optimize. */
  cursor: ${(props) => (props.$clickable ? "pointer" : "not-allowed")};
  pointer-events: ${(props) => (props.$clickable ? "auto" : "none")};
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.22);
  background: radial-gradient(
    120% 80% at 30% -10%,
    rgba(var(--glass-white-rgb), 0.92) 0%,
    rgba(var(--glass-pale-rgb), 0.78) 55%,
    rgba(var(--glass-pale-rgb), 0.66) 100%
  );
  /* blur dropped — multiplied N× per page. Alpha bumped above to keep
     the surface readable without backdrop blur. */
  box-shadow:
    0 4px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.85);
  transition:
    transform 240ms var(--ease-snappy),
    box-shadow 240ms var(--ease-snappy),
    border-color 240ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.5),
      0 1px 3px rgba(0, 0, 0, 0.3);
  }

  [data-theme="dark"] &::after {
    opacity: 0;
  }

  /* Thin bright crescent along the top rim — the "wet" glint */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 18px;
    width: 100%;
    border-radius: 999px;
    pointer-events: none;
    z-index: 3;
    background: linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.55) 0%,
      rgba(var(--glass-white-rgb), 0.42) 18%,
      rgba(var(--glass-white-rgb), 0.26) 42%,
      rgba(255, 255, 255, 0.12) 68%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  &:hover {
    box-shadow: ${(props) =>
      props.$clickable
        ? `0 14px 28px rgba(var(--color-brand-500-rgb), 0.24),
           0 4px 10px rgba(var(--color-brand-400-rgb), 0.16),
           inset 0 1px 0 rgba(255, 255, 255, 0.95)`
        : `0 4px 14px rgba(var(--color-brand-500-rgb), 0.14),
           inset 0 1px 0 rgba(255, 255, 255, 0.85)`};
    border-color: ${(props) =>
      props.$clickable
        ? "rgba(var(--color-brand-400-rgb), 0.48)"
        : "rgba(var(--color-brand-300-rgb), 0.22)"};
  }
`;

// ─── Media elements ──────────────────────────────────────────────────────

export const VideoElement = styled(Video)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  aspect-ratio: unset;
  z-index: 1;
  content-visibility: auto;
  backface-visibility: hidden;

  img,
  video {
    border-radius: inherit;
  }
`;

export const ImageElement = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.4s ease;
`;

/* Structured placeholder skeleton — mirrors the card's footer (model name
   bar + date chip) at the bottom. The card's own water gradient stays
   underneath; only the footer placeholders pulse between two close shades.
   No shimmer sweep, no full-card overlay. */

const skeletonShade = keyframes`
  0%, 100% {
    background: rgba(var(--glass-pale-rgb), 0.55);
  }
  50% {
    background: rgba(var(--glass-pale-rgb), 0.85);
  }
`;

export const SkeletonOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  bottom: 0;
  left: 0;
  align-items: center;
  height: 100%;
  padding: 8px;
  justify-content: flex-end;
  gap: 4px;
`;

export const SkeletonNameBar = styled.div`
  height: 14px;
  border-radius: 6px;
  animation: ${skeletonShade} 1.6s ease-in-out infinite;

  width: 100px;
`;

export const SkeletonDateChip = styled.div`
  width: 56px;
  height: 12px;
  border-radius: 4px;
  animation: ${skeletonShade} 1.6s ease-in-out infinite;
  animation-delay: 120ms;

  @media (max-width: 768px) {
    width: 48px;
  }
`;

// ─── Optional media-type badge (top-left) — frosted water pill ─────────

export const MediaBadgeWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 25;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  background: var(--glass-base-1);
  box-shadow:
    0 3px 10px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);

  svg {
    width: 13px;
    height: 13px;
    opacity: 0.82;
    path {
      stroke: var(--color-brand-700);
    }
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const MediaBadgeText = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

// ─── Select checkbox (top-right) — frosted water glass ────────────────

export const CheckboxContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 26;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 10px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  box-shadow:
    0 6px 14px rgba(var(--color-brand-600-rgb), 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
  cursor: pointer;
  pointer-events: auto;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Bottom info bar — light frosted water over media ──────────────────

export const VideoFooter = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 4px;
  padding: 9px 12px;
  pointer-events: none;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, transparent 100%);
  @media (max-width: 768px) {
    padding: 8px 10px;
  }
`;

export const ModelName = styled.div`
  flex: 1;
  min-width: 0;
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: normal;
  text-align: center;
`;

export const Date = styled.div`
  flex-shrink: 0;
  color: var(--glass-text-on-pill);

  opacity: 0.78;
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.005em;
  font-variant-numeric: tabular-nums;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

// ─── Loading / in-progress state — light water ──────────────────────────

export const LoadingImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  filter: blur(14px) brightness(0.78) saturate(1.15);
  transform: scale(1.08);
`;

/* Overlay — soft aqua wash over the blurred preview */
export const LoadingSpinnerBlur = styled.div`
  position: absolute;
  inset: 0;
  display: ${(props) => (props.$show ? "flex" : "none")};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(10px, 6cqi, 18px);
  z-index: 5;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    background: radial-gradient(
      110% 70% at 50% 50%,
      rgba(var(--color-brand-200-rgb), 0.28) 0%,
      rgba(var(--color-brand-400-rgb), 0.18) 45%,
      rgba(0, 0, 0, 0.22) 100%
    );
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    background: radial-gradient(
      110% 70% at 50% 50%,
      rgba(0, 0, 0, 0.18) 0%,
      rgba(0, 0, 0, 0.45) 100%
    );
  }
`;

/* Spinning water-pearl ring — scales with card width */
export const ProgressVisual = styled.div`
  position: relative;
  width: clamp(40px, 22cqi, 64px);
  height: clamp(40px, 22cqi, 64px);
  z-index: 1;
  flex-shrink: 0;
  filter: drop-shadow(0 6px 18px rgba(var(--color-brand-600-rgb), 0.35));
`;

export const ProgressRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    rgba(var(--color-brand-300-rgb), 0) 0deg,
    rgba(var(--color-brand-300-rgb), 0.2) 80deg,
    rgba(var(--color-brand-400-rgb), 0.6) 180deg,
    var(--color-brand-600) 320deg,
    rgba(var(--color-brand-600-rgb), 0) 360deg
  );
  mask: radial-gradient(circle, transparent 58%, black 60%);
  -webkit-mask: radial-gradient(circle, transparent 58%, black 60%);
  animation: ${ringSpin} 1.1s linear infinite;
`;

export const ProgressTrack = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(var(--color-brand-300-rgb), 0.24);
`;

export const ProgressCore = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40%;
  height: 40%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(14px, 8cqi, 22px);
  animation: ${corePulse} 1.6s ease-in-out infinite;
`;

/* Progress card — compact frosted glass pill centered over blurred preview */
export const ProgressCard = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(8px, 3.5cqi, 14px);
  padding: clamp(12px, 5cqi, 20px) clamp(14px, 6cqi, 24px);
  width: min(86%, 280px);
  max-width: 100%;
  border-radius: 16px;
  border: 1px solid var(--glass-edge);
  background: radial-gradient(
    120% 60% at 30% -10%,
    rgba(var(--glass-white-rgb), 0.94) 0%,
    rgba(var(--glass-pale-rgb), 0.88) 60%,
    rgba(var(--glass-pale-rgb), 0.8) 100%
  );
  backdrop-filter: blur(10px) saturate(160%);
  -webkit-backdrop-filter: blur(10px) saturate(160%);
  box-shadow:
    0 14px 32px -10px rgba(var(--color-brand-700-rgb), 0.35),
    0 4px 10px rgba(var(--color-brand-500-rgb), 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 10px rgba(var(--color-brand-200-rgb), 0.2);

  [data-theme="dark"] & {
    border-color: transparent;
    background: radial-gradient(
      120% 60% at 30% -10%,
      rgba(16, 22, 38, 0.78) 0%,
      rgba(12, 18, 32, 0.72) 60%,
      rgba(8, 14, 26, 0.7) 100%
    );
    box-shadow:
      0 14px 28px -8px rgba(0, 0, 0, 0.6),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 -4px 12px rgba(var(--color-brand-700-rgb), 0.22);
  }

  @container (max-width: 200px) {
    width: 92%;
    border-radius: 14px;
  }
`;

export const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 3px;
  border-radius: 999px;
  background: rgba(var(--color-brand-300-rgb), 0.22);
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--color-brand-600) 50%,
      transparent 100%
    );
    animation: ${shimmerSweep} 1.6s ease-in-out infinite;
  }
`;

export const RunningText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(3px, 1.6cqi, 6px);
  width: 100%;
  text-align: center;
`;

export const RunningTitle = styled.div`
  display: flex;
  justify-content: center;
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: clamp(11px, 4cqi, 13px);
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  [data-theme="dark"] & {
    color: var(--color-brand-300);
  }
`;

export const DotsSpan = styled.span`
  min-width: 12px;
  text-align: left;
  color: var(--color-brand-600);
`;

export const RunningMessage = styled.div`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: clamp(10px, 3.4cqi, 11px);
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.005em;

  @container (max-width: 180px) {
    display: none;
  }
`;

export const FreeMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(6px, 2.5cqi, 10px);
  width: 100%;
`;

// ─── Buttons ─────────────────────────────────────────────────────────────

/* Upgrade CTA — brand-gradient water pill */
export const UpgradeButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: clamp(30px, 11cqi, 38px);
  padding: 0 clamp(14px, 6cqi, 22px);
  font-size: clamp(11px, 3.6cqi, 13px) !important;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  box-shadow:
    0 10px 22px rgba(var(--color-brand-600-rgb), 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  margin-top: ${(props) => (props.$spacing ? "16px" : "8px")};
  width: min-content;
  align-self: center;
  flex-shrink: 0;
  cursor: pointer;
  pointer-events: auto;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 12px;
    right: 12px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.7) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Moving shimmer */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.38) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4s infinite;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* Retry — frosted light water pill */
export const RetryButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: clamp(28px, 10cqi, 36px);
  padding: 0 clamp(14px, 5cqi, 20px);
  margin-top: clamp(4px, 1.6cqi, 8px);
  font-size: clamp(11px, 3.5cqi, 12px) !important;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  border: none;
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.95) 0%,
    rgba(var(--glass-pale-rgb), 0.78) 55%,
    rgba(var(--glass-pale-rgb), 0.65) 100%
  );
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  pointer-events: auto;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Failed state — light water with red accent ────────────────────────

export const FailedOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(10px, 6cqi, 18px);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
      radial-gradient(
        circle at 50% 35%,
        rgba(255, 120, 143, 0.34) 0%,
        transparent 60%
      ),
      linear-gradient(
        180deg,
        rgba(var(--glass-pale-rgb), 0.55) 0%,
        rgba(255, 200, 212, 0.7) 100%
      );
    pointer-events: none;
  }

  [data-theme="dark"] &::after {
    background:
      radial-gradient(
        circle at 50% 35%,
        rgba(211, 71, 99, 0.34) 0%,
        transparent 60%
      ),
      linear-gradient(
        180deg,
        rgba(8, 16, 32, 0.65) 0%,
        rgba(60, 14, 22, 0.78) 100%
      );
  }
`;

export const FailedContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(6px, 2.5cqi, 10px);
  padding: clamp(12px, 5cqi, 20px) clamp(14px, 6cqi, 22px);
  width: min(86%, 280px);
  max-width: 100%;
  border-radius: 16px;
  border: 1px solid rgba(255, 120, 143, 0.4);
  background: radial-gradient(
    120% 60% at 30% -10%,
    rgba(var(--glass-white-rgb), 0.96) 0%,
    rgba(255, 238, 242, 0.9) 55%,
    rgba(255, 228, 234, 0.82) 100%
  );
  backdrop-filter: blur(10px) saturate(160%);
  -webkit-backdrop-filter: blur(10px) saturate(160%);
  box-shadow:
    0 14px 32px -10px rgba(211, 71, 99, 0.38),
    0 4px 10px rgba(211, 71, 99, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);

  [data-theme="dark"] & {
    border-color: rgba(255, 120, 143, 0.32);
    background: radial-gradient(
      120% 60% at 30% -10%,
      rgba(36, 16, 24, 0.92) 0%,
      rgba(28, 12, 20, 0.88) 55%,
      rgba(20, 10, 16, 0.84) 100%
    );
    box-shadow:
      0 14px 28px -8px rgba(0, 0, 0, 0.6),
      inset 0 -4px 12px rgba(211, 71, 99, 0.14);
  }

  @container (max-width: 200px) {
    width: 92%;
    border-radius: 14px;
  }
`;

export const FailedIconWrapper = styled.div`
  position: relative;
  width: clamp(34px, 16cqi, 46px);
  height: clamp(34px, 16cqi, 46px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 180, 195, 0.78) 0%,
    rgba(255, 120, 143, 0.5) 100%
  );
  border: 1px solid rgba(255, 120, 143, 0.55);
  box-shadow:
    0 4px 14px rgba(211, 71, 99, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);

  svg {
    width: 48%;
    height: 48%;
    path {
      stroke: #d34763;
      fill: #d34763;
    }
  }

  [data-theme="dark"] & {
    border-color: rgba(255, 120, 143, 0.4);
    background: radial-gradient(
      circle at 30% 30%,
      rgba(255, 138, 158, 0.52) 0%,
      rgba(211, 71, 99, 0.38) 100%
    );
    box-shadow:
      0 4px 14px rgba(0, 0, 0, 0.5),
      inset 0 -2px 6px rgba(0, 0, 0, 0.3);

    svg path {
      stroke: #ff7896;
      fill: #ff7896;
    }
  }
`;

export const FailedModelName = styled.div`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: clamp(9px, 3cqi, 10px);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: center;

  [data-theme="dark"] & {
    color: var(--color-brand-300);
  }

  @container (max-width: 180px) {
    display: none;
  }
`;

export const FailedTitle = styled.div`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: clamp(12px, 4.4cqi, 15px);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.01em;
  text-align: center;

  [data-theme="dark"] & {
    color: #ffffff;
  }
`;

export const FailedMessage = styled.div`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: clamp(10px, 3.4cqi, 11px);
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.005em;
  text-align: center;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.78);
  }

  @container (max-width: 180px) {
    br {
      display: none;
    }
  }
`;
